import { useEffect, useMemo, useRef, useState } from "react";
import { SearchInput } from "@/components/molecules/SearchInput";
import { Input } from "@/components/atoms/Input";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { MultiSelect } from "@/components/molecules/MultiSelect";
import { FilterBar, FilterBarLabel } from "@/components/molecules/FilterBar";
import { ScopePicker } from "@/components/molecules/ScopePicker";
import { Tooltip } from "@/components/atoms/Tooltip";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { contactsService } from "@/services/contactsService";
import type { LabelSummary } from "@/services/labelsService";
import type { ContactFilter, ContactFilterOptions, Option } from "@/types/entities";

interface Props {
  filter: ContactFilter;
  onChange: (next: Partial<ContactFilter>) => void;
  /** Resets every filter (not the sort order). */
  onClear: () => void;
  /** Display name of the label applied from the Labels page, when known. */
  labelFilterName?: string;
  /** Same Atlas labels the Manage Labels dialog uses — listed for this page's scope. */
  scopeLabels: LabelSummary[];
  loadingLabels?: boolean;
  /** Reloads labels when the entity/branch picker is applied. */
  onScopeApply?: () => void;
}

function contactFilterOptions(
  options: Option[] | undefined,
  emptyLabel: string,
  mapOption?: (option: Option) => Option,
): Option[] {
  return (options ?? []).map((option) => {
    const base = option.value === "" ? { ...option, label: emptyLabel } : option;
    return mapOption ? mapOption(base) : base;
  });
}

function parseBooleanFilterParam(value: string): boolean | undefined {
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}

function useDebouncedNumberFilter(
  value: number | undefined,
  onChange: (next: number | undefined) => void,
) {
  const [input, setInput] = useState(value != null ? String(value) : "");
  const debounced = useDebouncedValue(input, 300);

  useEffect(() => {
    setInput(value != null ? String(value) : "");
  }, [value]);

  useEffect(() => {
    const next = debounced.trim() ? Number(debounced) : undefined;
    if (value === next) return;
    if (value == null && next == null) return;
    if (Number.isNaN(next)) return;
    onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  return { input, setInput };
}

export function ContactsFilters({
  filter,
  onChange,
  onClear,
  labelFilterName,
  scopeLabels: labels,
  loadingLabels = false,
  onScopeApply,
}: Props) {
  const [options, setOptions] = useState<ContactFilterOptions | null>(null);

  useEffect(() => {
    let mounted = true;
    contactsService
      .getFilterOptions()
      .then((res) => {
        if (mounted) setOptions(res);
      })
      .catch((err) => console.error("Failed to load filter options", err));
    return () => {
      mounted = false;
    };
  }, []);

  const labelOptions = useMemo(() => {
    const options = labels.map((label) => ({
      value: String(label.id),
      label: label.name?.trim() || String(label.id),
      subtitle: label.labelDescription?.trim() || undefined,
    }));
    const known = new Set(options.map((o) => o.value));
    for (const id of filter.labelIds ?? []) {
      if (known.has(id)) continue;
      options.unshift({
        value: id,
        label: labelFilterName?.trim() || id,
        subtitle: undefined,
      });
      known.add(id);
    }
    return options;
  }, [labels, filter.labelIds, labelFilterName]);

  const [search, setSearch] = useState(filter.search ?? "");
  const debouncedSearch = useDebouncedValue(search, 300);
  const debouncedSearchRef = useRef(debouncedSearch);
  debouncedSearchRef.current = debouncedSearch;

  useEffect(() => {
    if ((filter.search ?? "") === debouncedSearch) return;
    onChange({ search: debouncedSearch || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // Mirror external filter changes (URL, Clear filters) without clobbering typing.
  useEffect(() => {
    const next = filter.search ?? "";
    setSearch((prev) => {
      if (prev === next) return prev;
      if (prev !== debouncedSearchRef.current) return prev;
      return next;
    });
  }, [filter.search]);

  const minBedroom = useDebouncedNumberFilter(filter.minBedroom, (next) => onChange({ minBedroom: next }));
  const priceFrom = useDebouncedNumberFilter(filter.priceFrom, (next) => onChange({ priceFrom: next }));
  const priceTo = useDebouncedNumberFilter(filter.priceTo, (next) => onChange({ priceTo: next }));

  return (
    <FilterBar
      className="bg-bridge-background"
      onClear={() => {
        // The numeric fields mirror `filter` through an effect, so they empty
        // themselves; the search box holds its own state and has to be told.
        setSearch("");
        onClear();
      }}
    >
      <FilterBarLabel />
      <ScopePicker page="contacts" onApply={onScopeApply} />
      <Tooltip side="top" hoverOnly content="Show contacts grouped under the labels you tick.">
        <div className="min-w-[180px] max-w-[240px]">
          <MultiSelect
            hideLabel
            enableSearch
            value={filter.labelIds ?? []}
            onChange={(ids) => onChange({ labelIds: ids.length > 0 ? ids : undefined })}
            options={labelOptions}
            placeholder="Any label"
            loading={loadingLabels}
            searchPlaceholder="Search labels..."
            emptyText="No labels in this account"
          />
        </div>
      </Tooltip>
      <Tooltip side="top" hoverOnly content="Search contacts by name, email, town or postcode.">
        <div>
          <SearchInput value={search} onChange={setSearch} placeholder="Search name, email, town, postcode..." />
        </div>
      </Tooltip>

      <Tooltip side="top" hoverOnly content="Show archived contacts, active contacts, or all.">
        <div className="min-w-[160px] max-w-[200px]">
          <MenuSelect
            hideLabel
            value={filter.archived === undefined ? "" : String(filter.archived)}
            onChange={(v) => onChange({ archived: parseBooleanFilterParam(v) })}
            options={contactFilterOptions(options?.archivedStates, "Any archive status")}
            placeholder="Any archive status"
          />
        </div>
      </Tooltip>

      {/* Sync status now filters from the Sync column's own header. */}

      {/* On narrower screens the requirement filters wrap to their own labelled row;
          on wide screens everything lines up in a single row and the label is hidden. */}
      <div className="basis-full 2xl:hidden" />
      <div className="flex 2xl:hidden items-center gap-2 text-bridge-muted min-w-[120px]">
        <span className="text-xs uppercase tracking-wide">Requirements</span>
      </div>
      <Tooltip side="top" hoverOnly content="Show contacts whose requirement is to buy or to rent.">
        <div className="min-w-[160px] max-w-[200px]">
          <MenuSelect
            hideLabel
            value={filter.profileIntention ?? ""}
            onChange={(v) => onChange({ profileIntention: v || undefined })}
            options={contactFilterOptions(options?.intentions, "Any intention")}
            placeholder="Any intention"
          />
        </div>
      </Tooltip>
      <Tooltip side="top" hoverOnly content="Show contacts by their preferred furnishing.">
        <div className="min-w-[180px] max-w-[220px]">
          <MenuSelect
            hideLabel
            value={filter.rentalIntentionFurnishing ?? ""}
            onChange={(v) => onChange({ rentalIntentionFurnishing: v || undefined })}
            options={contactFilterOptions(options?.furnishings, "Any furnishing")}
            placeholder="Any furnishing"
          />
        </div>
      </Tooltip>
      <Tooltip side="top" hoverOnly content="Show contacts requiring at least this many bedrooms.">
        <Input
          type="number"
          placeholder="Min beds"
          value={minBedroom.input}
          onChange={(e) => minBedroom.setInput(e.target.value)}
          className="w-28"
        />
      </Tooltip>
      <Tooltip side="top" hoverOnly content="Show contacts with a budget at or above this amount.">
        <Input
          type="number"
          placeholder="Min price"
          value={priceFrom.input}
          onChange={(e) => priceFrom.setInput(e.target.value)}
          className="w-32"
        />
      </Tooltip>
      <Tooltip side="top" hoverOnly content="Show contacts with a budget at or below this amount.">
        <Input
          type="number"
          placeholder="Max price"
          value={priceTo.input}
          onChange={(e) => priceTo.setInput(e.target.value)}
          className="w-32"
        />
      </Tooltip>
      <Tooltip side="top" hoverOnly content="Show contacts with active or inactive requirements.">
        <div className="min-w-[160px] max-w-[200px]">
          <MenuSelect
            hideLabel
            value={filter.isActiveProfile === undefined ? "" : String(filter.isActiveProfile)}
            onChange={(v) => onChange({ isActiveProfile: parseBooleanFilterParam(v) })}
            options={contactFilterOptions(options?.activeStates, "Any active status")}
            placeholder="Any active status"
          />
        </div>
      </Tooltip>
    </FilterBar>
  );
}
